import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

const API = `${window.location.origin}/api`;

// Poll status; give up after ~30s (15 × 2s) — Stripe usually flips within 2s
const MAX_POLL = 15;

export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [state, setState] = useState('checking'); // checking | paid | pending | error
  const [record, setRecord] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        setRecord(res.data);
        if (res.data.payment_status === 'paid') {
          setState('paid');
          return;
        }
        if (['failed', 'expired', 'cancelled', 'refunded'].includes(res.data.payment_status)) {
          setState('error');
          return;
        }
        if (++attempts >= MAX_POLL) {
          setState('pending');
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setState('error');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#050505] pt-20 flex items-center justify-center p-6" data-testid="payment-success-page">
      <div className="bg-[#0a0a0a] border border-white/10 max-w-lg w-full p-12 text-center">
        {state === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-[#D4AF37] mx-auto mb-6 animate-spin" />
            <h1 className="font-heading text-3xl text-white mb-4">Confirming payment…</h1>
            <p className="text-white/60">Just a moment while Stripe finalizes your transaction.</p>
          </>
        )}
        {state === 'paid' && (
          <>
            <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-6" />
            <h1 className="font-heading text-3xl text-white mb-4">Thank you.</h1>
            <p className="text-white/70 mb-2">
              We&rsquo;ve received your payment of <span className="text-[#D4AF37]">${Number(record?.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>.
            </p>
            <p className="text-white/50 text-sm mb-8">
              A receipt has been sent to your email. Our concierge team will be in touch with next steps.
            </p>
            <button
              onClick={() => navigate('/portal/invoices')}
              className="px-8 py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-widest hover:bg-[#C9A432]"
              data-testid="back-to-portal-btn"
            >
              Back to Portal
            </button>
          </>
        )}
        {state === 'pending' && (
          <>
            <Loader2 className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
            <h1 className="font-heading text-3xl text-white mb-4">Payment processing…</h1>
            <p className="text-white/60 mb-8">
              Stripe is still confirming your payment. You&rsquo;ll receive an email receipt once it clears — usually within a minute.
            </p>
            <button
              onClick={() => navigate('/portal/invoices')}
              className="px-8 py-3 border border-white/20 text-white text-xs uppercase tracking-widest hover:border-[#D4AF37]"
            >
              Back to Portal
            </button>
          </>
        )}
        {state === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
            <h1 className="font-heading text-3xl text-white mb-4">Something went wrong</h1>
            <p className="text-white/60 mb-8">We couldn&rsquo;t confirm your payment. Please check your invoice status or contact our team.</p>
            <button
              onClick={() => navigate('/portal/invoices')}
              className="px-8 py-3 border border-white/20 text-white text-xs uppercase tracking-widest hover:border-[#D4AF37]"
            >
              Back to Portal
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export const PaymentCancel = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#050505] pt-20 flex items-center justify-center p-6" data-testid="payment-cancel-page">
      <div className="bg-[#0a0a0a] border border-white/10 max-w-lg w-full p-12 text-center">
        <XCircle className="w-16 h-16 text-white/40 mx-auto mb-6" />
        <h1 className="font-heading text-3xl text-white mb-4">Payment cancelled</h1>
        <p className="text-white/60 mb-8">Your invoice is still open. You can complete payment anytime from your portal.</p>
        <button
          onClick={() => navigate('/portal/invoices')}
          className="px-8 py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-widest hover:bg-[#C9A432]"
        >
          Back to Portal
        </button>
      </div>
    </div>
  );
};
